import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Package, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { CriarReceituarioDtoTipo, type ReceituarioResponse } from '@/api/generated/model';
import { usePacientesControllerListar } from '@/api/generated/pacientes/pacientes';
import {
  getReceituariosControllerListarQueryKey,
  useReceituariosControllerAtualizar,
  useReceituariosControllerCriar,
  useReceituariosControllerListarTemplates,
} from '@/api/generated/receituarios/receituarios';
import { ApiError } from '@/api/http-client';
import { useAuth } from '@/features/auth/hooks/useAuth';
import {
  ImportarProdutoDialogo,
  type ProdutoImportado,
} from '@/features/receituarios/components/ImportarProdutoDialogo';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { enderecoCompleto } from '@/shared/lib/endereco';
import { EstadoVazio } from '@/shared/components/estados';
import { ROTAS } from '@/shared/lib/rotas';
import { cn } from '@/shared/lib/cn';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { toast } from '@/shared/ui/sonner';
import { Textarea } from '@/shared/ui/textarea';

interface Item {
  descricao: string;
  posologia: string | null;
  quantidade: string | null;
  concentracao: string | null;
  formaFarmaceutica: string | null;
  produtoParceiroId: string | null;
  parceiroNome: string | null;
}

type Tipo = (typeof CriarReceituarioDtoTipo)[keyof typeof CriarReceituarioDtoTipo];

const TIPOS: { valor: Tipo; rotulo: string; descricao: string }[] = [
  { valor: CriarReceituarioDtoTipo.SIMPLES, rotulo: 'Simples', descricao: 'Receituário comum, uma via.' },
  {
    valor: CriarReceituarioDtoTipo.CONTROLE_ESPECIAL,
    rotulo: 'Controle especial',
    descricao: 'Modelo eletrônico da Anvisa: número do SNCR, seu endereço e QR de verificação.',
  },
];

const ITEM_VAZIO: Item = {
  descricao: '',
  posologia: null,
  quantidade: null,
  concentracao: null,
  formaFarmaceutica: null,
  produtoParceiroId: null,
  parceiroNome: null,
};

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  /** Ausente = criação. */
  receituario?: ReceituarioResponse;
}

export function ReceituarioFormDialogo({ aberto, aoFechar, receituario }: Props) {
  const queryClient = useQueryClient();
  const { pode } = usePermissoes();
  const criar = useReceituariosControllerCriar();
  const atualizar = useReceituariosControllerAtualizar();
  const editando = Boolean(receituario);
  const { me } = useAuth();

  const [pacienteId, setPacienteId] = useState('');
  const [tipo, setTipo] = useState<Tipo>(CriarReceituarioDtoTipo.SIMPLES);
  const [numeroSncr, setNumeroSncr] = useState('');
  const [conteudo, setConteudo] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [itens, setItens] = useState<Item[]>([]);
  const [importando, setImportando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const { data: pacientes } = usePacientesControllerListar(
    { pagina: 1, porPagina: 100, ordenarPor: 'nome', ordem: 'asc' },
    { query: { enabled: aberto && !editando } },
  );

  const { data: templates } = useReceituariosControllerListarTemplates({ query: { enabled: aberto } });

  // O médico só importa produto se puder ver a vitrine: o caminho passa por
  // `GET /v1/parceiros`, guardado por `parceiros.visualizar`.
  const podeImportar = pode('parceiros.visualizar');

  useEffect(() => {
    if (!aberto) return;

    setPacienteId(receituario?.pacienteId ?? '');
    setTipo((receituario?.tipo as Tipo | undefined) ?? CriarReceituarioDtoTipo.SIMPLES);
    setNumeroSncr(receituario?.numeroSncr ?? '');
    setConteudo(receituario?.conteudo ?? '');
    setObservacoes(receituario?.observacoes ?? '');
    setItens(
      receituario?.itens.map((i) => ({
        descricao: i.descricao,
        posologia: i.posologia,
        quantidade: i.quantidade,
        concentracao: i.concentracao,
        formaFarmaceutica: i.formaFarmaceutica,
        produtoParceiroId: i.produtoParceiroId,
        parceiroNome: i.parceiroNome,
      })) ?? [],
    );
    setErro(null);
  }, [aberto, receituario]);

  function aplicarTemplate(id: string) {
    const template = templates?.itens.find((t) => t.id === id);
    if (!template) return;

    // Acrescenta ao que já existe em vez de substituir: o texto digitado até
    // aqui é trabalho, e modelo que apaga o que a pessoa escreveu vira uma
    // armadilha que só se descobre depois de perder.
    setConteudo((atual) => (atual.trim() ? `${atual}\n\n${template.conteudo}` : template.conteudo));
    toast.success(`Modelo "${template.nome}" aplicado.`);
  }

  function editarItem(indice: number, campo: keyof Item, valor: string) {
    setItens((atual) =>
      atual.map((item, i) =>
        i === indice ? { ...item, [campo]: campo === 'descricao' ? valor : valor || null } : item,
      ),
    );
  }

  function importar(produto: ProdutoImportado) {
    setItens((atual) => [
      ...atual,
      { ...produto, concentracao: null, formaFarmaceutica: null, parceiroNome: null },
    ]);
  }

  const controleEspecial = tipo === CriarReceituarioDtoTipo.CONTROLE_ESPECIAL;

  async function salvar() {
    setErro(null);

    if (!editando && !pacienteId) {
      setErro('Escolha o paciente.');
      return;
    }

    const preenchidos = itens.filter((i) => i.descricao.trim());
    if (preenchidos.length === 0 && !conteudo.trim()) {
      setErro('Escreva o receituário ou acrescente ao menos um item.');
      return;
    }

    const corpoItens = preenchidos.map((i) => ({
      descricao: i.descricao.trim(),
      posologia: i.posologia?.trim() || null,
      quantidade: i.quantidade?.trim() || null,
      concentracao: i.concentracao?.trim() || null,
      formaFarmaceutica: i.formaFarmaceutica?.trim() || null,
      produtoParceiroId: i.produtoParceiroId,
    }));

    const comuns = {
      tipo,
      numeroSncr: numeroSncr.trim() || null,
      conteudo: conteudo.trim(),
      observacoes: observacoes.trim() ? observacoes.trim() : null,
      itens: corpoItens,
    };

    setEnviando(true);
    try {
      if (receituario) {
        await atualizar.mutateAsync({ id: receituario.id, data: comuns });
      } else {
        await criar.mutateAsync({ data: { pacienteId, ...comuns } });
      }

      await queryClient.invalidateQueries({ queryKey: getReceituariosControllerListarQueryKey() });
      toast.success(editando ? 'Receituário atualizado.' : 'Receituário criado.');
      aoFechar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não foi possível salvar o receituário.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editando ? 'Editar receituário' : 'Novo receituário'}</DialogTitle>
            <DialogDescription>
              Escreva o texto, use um modelo pronto ou monte a lista de itens.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            {erro && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {erro}
              </div>
            )}

            {editando ? (
              <div className="rounded-lg bg-muted/50 px-4 py-3 flex justify-between text-sm">
                <span className="text-muted-foreground">Paciente</span>
                <span className="font-medium text-foreground">{receituario?.pacienteNome}</span>
              </div>
            ) : (
              <Campo id="pacienteId" rotulo="Paciente" obrigatorio>
                <Select value={pacienteId} onValueChange={setPacienteId}>
                  <SelectTrigger id="pacienteId" className={CLASSE_CONTROLE} aria-label="Paciente">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {pacientes?.itens.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Campo>
            )}

            <fieldset>
              <legend className="text-sm font-medium text-brand mb-2">Tipo de receituário</legend>
              <div role="radiogroup" aria-label="Tipo de receituário" className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {TIPOS.map((opcao) => (
                  <button
                    key={opcao.valor}
                    type="button"
                    role="radio"
                    aria-checked={tipo === opcao.valor}
                    onClick={() => setTipo(opcao.valor)}
                    className={cn(
                      'rounded-lg border-2 px-3 py-2 text-left transition-colors',
                      tipo === opcao.valor
                        ? 'border-brand bg-brand/10'
                        : 'border-border hover:border-brand/40',
                    )}
                  >
                    <span className="block text-sm font-semibold text-foreground">{opcao.rotulo}</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">{opcao.descricao}</span>
                  </button>
                ))}
              </div>
            </fieldset>

            {/* Avisa ANTES de assinar: sem o endereço o backend recusa a
                assinatura, e descobrir isso só no último clique é frustrante. */}
            {controleEspecial && (
              <Campo
                id="numeroSncr"
                rotulo="N.º da receita no SNCR"
                dica="Obtenha a numeração em sncr.anvisa.gov.br. Pode salvar o rascunho sem ela, mas é exigida para assinar."
              >
                <Input
                  id="numeroSncr"
                  value={numeroSncr}
                  onChange={(e) => setNumeroSncr(e.target.value)}
                  maxLength={40}
                  className={CLASSE_CONTROLE}
                />
              </Campo>
            )}

            {controleEspecial &&
              (receituario?.veterinarioId ?? me?.usuario.id) === me?.usuario.id &&
              !enderecoCompleto(me?.perfil.endereco) && (
                <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    O receituário de controle especial exige o endereço da sua clínica, que ainda não está completo.
                    Você pode salvar o rascunho, mas para assinar preencha logradouro, número, cidade e UF em{' '}
                    <Link to={ROTAS.perfil} className="font-medium underline" onClick={aoFechar}>
                      Perfil
                    </Link>
                    .
                  </span>
                </div>
              )}

            {templates && templates.itens.length > 0 && (
              <Campo
                id="template"
                rotulo="Aplicar modelo"
                dica="O texto do modelo é ACRESCENTADO ao que já está escrito."
              >
                <Select value="" onValueChange={aplicarTemplate}>
                  <SelectTrigger id="template" className={CLASSE_CONTROLE} aria-label="Aplicar modelo">
                    <SelectValue placeholder="Escolha um modelo" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.itens.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Campo>
            )}

            <Campo id="conteudo" rotulo="Receituário">
              <Textarea
                id="conteudo"
                rows={6}
                value={conteudo}
                onChange={(e) => setConteudo(e.target.value)}
                className={CLASSE_CONTROLE}
              />
            </Campo>

            <div className="space-y-3 rounded-lg border border-brand/20 p-4">
              <div className="flex items-center justify-between gap-3">
                <h4 className="text-sm font-semibold text-brand uppercase tracking-wide">Itens</h4>
                <div className="flex gap-2">
                  {podeImportar && (
                    <Button type="button" variant="outline" size="sm" onClick={() => setImportando(true)}>
                      <Package className="w-4 h-4" />
                      Importar de parceiro
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setItens((atual) => [...atual, { ...ITEM_VAZIO }])}
                  >
                    <Plus className="w-4 h-4" />
                    Item
                  </Button>
                </div>
              </div>

              {itens.length === 0 ? (
                <EstadoVazio titulo="Nenhum item" descricao="O receituário pode ser só texto." className="py-6" />
              ) : (
                <ul className="space-y-3">
                  {itens.map((item, indice) => (
                    <li key={indice} className="space-y-2 rounded-md bg-muted/40 p-3">
                      <div className="flex items-center gap-2">
                        <Input
                          value={item.descricao}
                          onChange={(e) => editarItem(indice, 'descricao', e.target.value)}
                          placeholder="Medicamento"
                          className={CLASSE_CONTROLE}
                          aria-label={`Descrição do item ${indice + 1}`}
                        />
                        {item.produtoParceiroId && <Badge variant="outline">de parceiro</Badge>}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={`Remover item ${indice + 1}`}
                          onClick={() => setItens((atual) => atual.filter((_, i) => i !== indice))}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      {/* Campos próprios do modelo da Anvisa; o simples não os imprime. */}
                      {controleEspecial && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <Input
                            value={item.concentracao ?? ''}
                            onChange={(e) => editarItem(indice, 'concentracao', e.target.value)}
                            placeholder="Concentração (ex.: 100 mg)"
                            className={CLASSE_CONTROLE}
                            aria-label={`Concentração do item ${indice + 1}`}
                          />
                          <Input
                            value={item.formaFarmaceutica ?? ''}
                            onChange={(e) => editarItem(indice, 'formaFarmaceutica', e.target.value)}
                            placeholder="Forma farmacêutica (ex.: comprimido)"
                            className={CLASSE_CONTROLE}
                            aria-label={`Forma farmacêutica do item ${indice + 1}`}
                          />
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Input
                          value={item.posologia ?? ''}
                          onChange={(e) => editarItem(indice, 'posologia', e.target.value)}
                          placeholder="Posologia"
                          className={CLASSE_CONTROLE}
                          aria-label={`Posologia do item ${indice + 1}`}
                        />
                        <Input
                          value={item.quantidade ?? ''}
                          onChange={(e) => editarItem(indice, 'quantidade', e.target.value)}
                          placeholder="Quantidade"
                          className={CLASSE_CONTROLE}
                          aria-label={`Quantidade do item ${indice + 1}`}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <Campo id="observacoes" rotulo="Observações">
              <Textarea
                id="observacoes"
                rows={2}
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                className={CLASSE_CONTROLE}
              />
            </Campo>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar}>
              Cancelar
            </Button>
            <Button onClick={() => void salvar()} disabled={enviando}>
              {enviando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editando ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportarProdutoDialogo
        aberto={importando}
        aoFechar={() => setImportando(false)}
        aoImportar={importar}
      />
    </>
  );
}
